import AddRoundedIcon from "@mui/icons-material/AddRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import AppBottomSheet from "./AppBottomSheet";
import AppSearchField from "./AppSearchField";
import AppCard from "./AppCard";
import { APP_UI } from "../ui/appUi";

export interface SelectableUserName {
  id: number;
  name: string;
  is_favorite: boolean;
}

export default function PersonSelectionSheet({
  open,
  names,
  selected,
  onSelect,
  onCreate,
  onClose,
}: {
  open: boolean;
  names: SelectableUserName[];
  selected: string[];
  onSelect: (name: string) => void;
  onCreate: (name: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const cleanQuery = query.trim().replace(/\s+/g, " ");
  const normalizedQuery = cleanQuery.toLocaleLowerCase("de-DE");
  const filtered = names.filter(
    (item) =>
      !normalizedQuery
      || item.name.toLocaleLowerCase("de-DE").includes(normalizedQuery)
  );
  const exactMatch = cleanQuery.length > 0 && names.some(
    (item) => item.name.toLocaleLowerCase("de-DE") === cleanQuery.toLocaleLowerCase("de-DE")
  );

  return (
    <AppBottomSheet
      open={open}
      onClose={() => {
        setQuery("");
        onClose();
      }}
      title="Person auswählen"
      subtitle={`${selected.length} ausgewählt`}
    >
      <AppSearchField value={query} onChange={setQuery} placeholder="Name suchen oder neu eingeben" autoFocus />

      {cleanQuery.length > 0 && !exactMatch && (
        <Button
          variant="outlined"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            onCreate(cleanQuery);
            setQuery("");
          }}
          sx={{
            minHeight: 48,
            borderRadius: 999,
            textTransform: "none",
            color: APP_UI.accent,
            borderColor: APP_UI.accentBorder,
            fontWeight: 850,
            justifyContent: "flex-start",
            px: 1.6,
          }}
        >
          „{cleanQuery}“ übernehmen
        </Button>
      )}

      <Stack spacing={0.65}>
        {filtered.map((item) => {
          const active = selected.includes(item.name);
          return (
            <AppCard
              key={item.id}
              component="button"
              type="button"
              onClick={() => onSelect(item.name)}
              role="button"
              sx={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: active ? APP_UI.accentSoft : APP_UI.card,
                borderColor: active ? APP_UI.accentBorder : APP_UI.border,
                color: "inherit",
                font: "inherit",
              }}
            >
              {item.is_favorite && <StarRoundedIcon sx={{ color: APP_UI.warning, fontSize: 20, flex: "0 0 auto" }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: APP_UI.text, fontWeight: active ? 850 : 700 }}>{item.name}</Typography>
                {item.is_favorite && <Typography variant="caption" sx={{ color: APP_UI.warning }}>Favorit</Typography>}
              </Box>
              {active && <Typography variant="caption" sx={{ color: APP_UI.accent, fontWeight: 850 }}>Ausgewählt</Typography>}
            </AppCard>
          );
        })}
        {filtered.length === 0 && cleanQuery.length === 0 && (
          <Typography variant="body2" sx={{ color: APP_UI.textMuted, textAlign: "center", py: 3 }}>
            Keine Namen vorhanden.
          </Typography>
        )}
      </Stack>
    </AppBottomSheet>
  );
}
